import { Box, Link, ListItem, Text } from '@chakra-ui/react'
import parse, {
  attributesToProps,
  DOMNode,
  domToReact,
  Element,
  HTMLReactParserOptions
} from 'html-react-parser'
import { ReactElement } from 'react'
import { OL, UL } from '../lists'
import { BoldText, H1, H2, H3, H4, H5, H6 } from '../typography'

type HtmlElementProps = Omit<Element, 'parent'> & { parent: Element }

interface HtmlStringParserProps {
  htmlString: string
}

interface NodeComponentMap {
  [domNodeName: string]: (domNode: HtmlElementProps) => ReactElement | null
}

const nodeComponentMap: NodeComponentMap = Object.freeze({
  p: (domNode) => (
    <Text>
      {domToReact((domNode as Element).children as DOMNode[], options)}
    </Text>
  ),
  strong: (domNode) => (
    <BoldText as='span'>
      {domToReact((domNode as Element).children as DOMNode[], options)}
    </BoldText>
  ),
  h1: (domNode) => (
    <H1>{domToReact((domNode as Element).children as DOMNode[], options)}</H1>
  ),
  h2: (domNode) => (
    <H2>{domToReact((domNode as Element).children as DOMNode[], options)}</H2>
  ),
  h3: (domNode) => (
    <H3>{domToReact((domNode as Element).children as DOMNode[], options)}</H3>
  ),
  h4: (domNode) => (
    <H4>{domToReact((domNode as Element).children as DOMNode[], options)}</H4>
  ),
  h5: (domNode) => (
    <H5>{domToReact((domNode as Element).children as DOMNode[], options)}</H5>
  ),
  h6: (domNode) => (
    <H6>{domToReact((domNode as Element).children as DOMNode[], options)}</H6>
  ),
  ul: (domNode) => (
    <UL>{domToReact((domNode as Element).children as DOMNode[], options)}</UL>
  ),
  ol: (domNode) => (
    <OL>{domToReact((domNode as Element).children as DOMNode[], options)}</OL>
  ),
  li: (domNode) => (
    <ListItem>
      {domToReact((domNode as Element).children as DOMNode[], options)}
    </ListItem>
  ),
  span: (domNode) => (
    <Box as='span'>
      {domToReact((domNode as Element).children as DOMNode[], options)}
    </Box>
  ),
  a: (domNode) => (
    <Link {...attributesToProps(domNode.attribs)}>
      {domToReact((domNode as Element).children as DOMNode[], options)}
    </Link>
  )
})

const options: HTMLReactParserOptions = {
  replace: (domNode: HtmlElementProps) => {
    if (domNode.attribs && nodeComponentMap[domNode.name]) {
      /* if the dom node is a list item and it's not wrapped by a ul or ol tag
         then turn it into a paragraph to prevent chakra from throwing an error */
      if (domNode.name === 'li') {
        if (
          !domNode.parent ||
          (domNode.parent?.name !== 'ul' && domNode.parent?.name !== 'ol')
        ) {
          return nodeComponentMap['p'](domNode)
        }
      }

      return nodeComponentMap[domNode.name](domNode)
    }

    return null
  }
} as HTMLReactParserOptions

const HtmlStringParser = ({ htmlString = '' }: HtmlStringParserProps) => {
  if (!htmlString) {
    return null
  }

  return <>{parse(htmlString, options)}</>
}

export default HtmlStringParser
